<template>
  <div class="system-prompt">
    <div class="system-prompt-content">
      <div v-if="isLoading" class="loading">
        Ladataan järjestelmäkehotetta...
      </div>

      <div v-else-if="error" class="error">
        <p>Virhe järjestelmäkehoteen lataamisessa: {{ error }}</p>
        <button @click="fetchSystemPrompt" class="retry-button">
          Yritä uudelleen
        </button>
      </div>

      <div v-else class="prompt-editor">
        <div class="prompt-preview" v-if="!isEditing">
          <div v-html="renderedPrompt"></div>
          <button @click="startEditing" class="edit-button">Muokkaa</button>
        </div>

        <div v-else class="prompt-edit">
          <textarea
            v-model="editablePrompt"
            class="prompt-textarea"
            placeholder="Kirjoita järjestelmäkehote tähän..."
          ></textarea>
          <div class="edit-actions">
            <button @click="saveChanges" class="save-button">Tallenna</button>
            <button @click="cancelEditing" class="cancel-button">
              Peruuta
            </button>
            <button @click="resetToDefault" class="reset-button">
              Palauta oletukseen
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAuthStore } from "@/stores";
import { marked } from "marked";
import { computed, defineExpose, onMounted, ref } from "vue";

interface SystemPromptResponse {
  systemPrompt: string;
  error?: string;
}

const props = defineProps<{
  assessmentId?: string;
}>();

const authStore = useAuthStore();

// No longer emitting close event - handled by parent

const isLoading = ref(false);
const error = ref<string | null>(null);
const originalPrompt = ref("");
const editablePrompt = ref("");
const isEditing = ref(false);

// Helper functions for profile preferences
const getSavedPrompt = (): string | undefined => {
  return authStore.currentProfile?.state?.preferences?.systemPrompt;
};

const updatePromptPreference = (prompt: string | null) => {
  if (authStore.currentProfile) {
    const currentPreferences = authStore.currentProfile.state.preferences || {};
    const updatedProfile = {
      ...authStore.currentProfile,
      state: {
        ...authStore.currentProfile.state,
        preferences: {
          ...currentPreferences,
          systemPrompt: prompt,
        },
      },
    };
    authStore.setProfile(updatedProfile);
  }
};

const renderedPrompt = computed(() => {
  return marked(editablePrompt.value || "");
});

const fetchSystemPrompt = async () => {
  isLoading.value = true;
  error.value = null;

  try {
    const apiBaseUrl =
      (process.env.VUE_APP_API_BASE_URL as string) ||
      "http://localhost:3000/api";
    const assessmentId = props.assessmentId || "1"; // Fallback to "1" if no prop provided
    const response = await fetch(
      `${apiBaseUrl}/system-prompt/${assessmentId}`,
      {
        credentials: "include",
      },
    );

    if (!response.ok) {
      throw new Error("Järjestelmäkehoteen lataus epäonnistui");
    }

    const data = (await response.json()) as SystemPromptResponse;

    if (data.error) {
      throw new Error(data.error);
    }

    originalPrompt.value = data.systemPrompt;

    // Try to load saved version from profile state
    try {
      const savedPrompt = getSavedPrompt();
      editablePrompt.value = savedPrompt || originalPrompt.value;
    } catch (err) {
      console.warn(
        "Failed to load system prompt from profile state, using original:",
        err,
      );
      editablePrompt.value = originalPrompt.value;
    }
  } catch (err) {
    console.error("Error fetching system prompt:", err);
    error.value = err instanceof Error ? err.message : "Tuntematon virhe";
  } finally {
    isLoading.value = false;
  }
};

const startEditing = () => {
  isEditing.value = true;
};

const saveChanges = () => {
  // Save to profile state
  updatePromptPreference(editablePrompt.value);
  isEditing.value = false;
};

const cancelEditing = () => {
  // Revert to saved version
  try {
    const savedPrompt = getSavedPrompt();
    editablePrompt.value = savedPrompt || originalPrompt.value;
  } catch (err) {
    console.warn("Failed to load saved prompt, using original:", err);
    editablePrompt.value = originalPrompt.value;
  }
  isEditing.value = false;
};

const resetToDefault = () => {
  editablePrompt.value = originalPrompt.value;
  updatePromptPreference(null);
  isEditing.value = false;
};

// Close functionality now handled by parent component

// Expose method for external reset (when "Palauta oletustilaan" is clicked)
const resetSystemPrompt = () => {
  editablePrompt.value = originalPrompt.value;
  updatePromptPreference(null);
  isEditing.value = false;
};

// Expose method to get current prompt for API calls
const getCurrentPrompt = () => {
  return editablePrompt.value;
};

onMounted(() => {
  void fetchSystemPrompt();
});

// Expose methods for parent component
defineExpose({
  resetSystemPrompt,
  getCurrentPrompt,
});
</script>

<style scoped>
.system-prompt {
  padding: 1rem;
}

/* Header styling removed - now handled by SectionWrapper */

.loading,
.error {
  padding: 2rem;
  text-align: center;
  color: #666;
}

.error {
  color: #dc3545;
}

.retry-button {
  margin-top: 1rem;
  padding: 0.5rem 1rem;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.retry-button:hover {
  background-color: #0056b3;
}

.prompt-preview {
  position: relative;
}

.edit-button {
  margin-top: 1rem;
  padding: var(--space-sm) var(--space-md);
  border: 1px solid var(--color-primary);
  background: var(--color-primary);
  color: var(--color-text-white);
  border-radius: var(--radius-md);
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  box-shadow: var(--shadow-sm);
  transition: all 0.2s ease-in-out;
}

.edit-button:hover {
  background: var(--color-primary-dark);
  border-color: var(--color-primary-dark);
  box-shadow: var(--shadow-md);
  transform: translateY(-1px);
}

.prompt-textarea {
  width: 100%;
  min-height: 400px;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 1rem;
  font-family: "Courier New", monospace;
  font-size: 0.9rem;
  resize: vertical;
}

.edit-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
}

.save-button {
  padding: 0.5rem 1rem;
  background-color: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.save-button:hover {
  background-color: #218838;
}

.cancel-button {
  padding: 0.5rem 1rem;
  background-color: #6c757d;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.cancel-button:hover {
  background-color: #545b62;
}

.reset-button {
  padding: 0.5rem 1rem;
  background-color: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.reset-button:hover {
  background-color: #c82333;
}

/* Markdown styling */
:deep(h1),
:deep(h2),
:deep(h3),
:deep(h4),
:deep(h5),
:deep(h6) {
  margin-top: 1.5rem;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #333;
}

:deep(h1) {
  font-size: 1.5rem;
}
:deep(h2) {
  font-size: 1.3rem;
}
:deep(h3) {
  font-size: 1.1rem;
}
:deep(h4) {
  font-size: 1rem;
}

:deep(p) {
  margin: 0.5rem 0;
  line-height: 1.6;
}

:deep(ul),
:deep(ol) {
  padding-left: 1.5rem;
  margin: 0.5rem 0;
}

:deep(li) {
  margin-bottom: 0.25rem;
}

:deep(strong) {
  font-weight: 600;
}

:deep(em) {
  font-style: italic;
}

:deep(code) {
  background-color: #f8f9fa;
  padding: 0.2rem 0.4rem;
  border-radius: 3px;
  font-family: "Courier New", monospace;
  font-size: 0.9em;
}
</style>
